import { memo, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { sidebarApi } from '@/lib/api'
import { getDefaultSidebar } from '@/lib/navigation/defaultSidebar'
import { endpointForPath } from '@/lib/navigation/endpointAccess'
import { SidebarApiIcon } from '@/components/ui/app-icon'
import { LogOut, Navigation, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Sidebar = memo(function Sidebar() {
  const location = useLocation()
  const role = useAuthStore((s) => s.role)
  const roleName = useAuthStore((s) => s.roleName)
  const userId = useAuthStore((s) => s.userId)
  const permissionsConfigured = useAuthStore((s) => s.permissionsConfigured)
  const effectiveEndpoints = useAuthStore((s) => s.effectiveEndpoints)
  const sidebarOpen = useUIStore((s) => s.sidebarOpen)
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)

  const { data: serverItems } = useQuery({
    queryKey: ['sidebar', role, userId],
    queryFn: async () => (await sidebarApi.get(role ?? undefined)).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const items = useMemo(() => {
    if (serverItems !== undefined) return serverItems
    const defaults = getDefaultSidebar(role)
    if (!userId || !role || !permissionsConfigured) return defaults
    return defaults.filter((item) => {
      const endpoint = endpointForPath(item.path, role)
      return endpoint !== null && effectiveEndpoints?.includes(endpoint)
    })
  }, [serverItems, role, userId, permissionsConfigured, effectiveEndpoints])

  // Close mobile drawer after navigation
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname, setSidebarOpen])

  useEffect(() => {
    if (!sidebarOpen) return
    const isMobile = () => window.matchMedia('(max-width: 767px)').matches
    if (!isMobile()) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [sidebarOpen, setSidebarOpen])

  const isActive = (item: { path: string; match: string[] }) => {
    const rootPath = role === 'admin' ? '/admin' : '/worker'
    return item.match.some((m) => {
      if (m === rootPath) return location.pathname === m
      return location.pathname.startsWith(m)
    })
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden',
          sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={() => setSidebarOpen(false)}
        aria-hidden={!sidebarOpen}
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-full w-[min(16.5rem,88vw)] flex-col border-r border-gray-200 bg-white shadow-xl transition-transform duration-200 ease-out',
          'md:static md:z-auto md:w-60 md:shrink-0 md:translate-x-0 md:shadow-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Навигация"
      >
        {/* Logo */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
          <Link
            to={role === 'admin' ? '/admin' : '/worker/tasks'}
            className="flex items-center gap-2 no-underline"
          >
            <img src="/TrueMark logo.svg" alt="TrueMark" className="h-7 w-auto" />
            <span className="text-sm font-semibold text-gray-900">TrueMark</span>
          </Link>
          <button
            type="button"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Закрыть меню"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
          {items.map((item) => {
            const cls = cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium no-underline transition-colors',
              isActive(item)
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            )

            return (
              <Link key={item.path} to={item.path} className={cls}>
                <SidebarApiIcon icon={item.icon} label={item.label} className="h-4 w-4 shrink-0" />
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
              {roleName}
            </span>
            <div className="flex items-center gap-1">
              {role === 'admin' && (!permissionsConfigured || effectiveEndpoints?.includes('advanced')) && (
                <Link
                  to="/admin/sidebar-settings"
                  className={cn(
                    'rounded p-1.5 transition-colors',
                    location.pathname === '/admin/sidebar-settings'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                  )}
                  title="Настройки навигации"
                >
                  <Navigation className="h-4 w-4" />
                </Link>
              )}
              <Link
                to="/login"
                className="rounded p-1.5 text-gray-400 no-underline transition-colors hover:bg-red-50 hover:text-red-600"
                title="Выйти"
                onClick={() => {
                  useAuthStore.getState().clearAuth()
                }}
              >
                <LogOut className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
})
